export const PRESET_LABELS = {
  hq_sales: "HQ sales",
  os_admin: "OS admin",
  hr: "HR pack",
  ops_manager: "Ops manager pack (T3)",
  brand_manager: "Brand manager pack (T2)",
  store_manager: "Store manager pack (T1)",
  chef: "Chef pack",
  crew: "Employee pack",
  super_admin: "Super admin",
};

export const PRESET_BLURBS = {
  hq_sales: "Stiq OS live sales and Copilot.",
  os_admin: "Stiq OS admin, including Identity.",
  hr: "SoftOne / Ergani upload modules. HQ email only.",
  ops_manager: "All-store schedule approve modules. HQ email only.",
  brand_manager: "One-brand Ops modules. HQ email only.",
  store_manager: "One store, or a UNIT site (PG + DiNAS) that expands to both brands.",
  chef: "Employee floor plus recipes and end-of-day stock.",
  crew: "Own rota and leave only.",
  super_admin: "Full OS and Ops.",
};

export const OPS_ROLE_OPTIONS = [
  { id: "hr", label: "HR", limited_ok: false },
  { id: "ops_manager", label: "Ops manager (T3)", limited_ok: false },
  { id: "brand_manager", label: "Brand manager (T2)", limited_ok: false },
  { id: "store_manager", label: "Store manager (T1)", limited_ok: true },
  { id: "employee", label: "Employee", limited_ok: true },
  { id: "", label: "None" },
];

export const OPS_ROLE_BLURBS = {
  employee: "Store staff. Nav and actions follow Ops permissions.",
  store_manager:
    "Builds and sends rotas. Pick one brand + store, or PG + DiNAS and a UNIT site (assigns both stores).",
  brand_manager: "Approves rotas for one brand (PG or DiNAS). HQ email only.",
  ops_manager: "Approves rotas for all stores. HQ email only.",
  hr: "SoftOne / Ergani upload and final rota approval. HQ email only.",
};

/** Required Ops module → minimum level for each Role. */
export const OPS_ROLE_REQUIRED_MODULES = {
  employee: {
    schedule: "view",
    leave: "edit",
    announcements: "view",
  },
  store_manager: {
    schedule: "edit",
    shift: "edit",
    leave: "edit",
    procureiq: "edit",
    announcements: "edit",
    eod_stock: "edit",
  },
  brand_manager: {
    schedule: "edit",
    shift: "edit",
    leave: "edit",
    procureiq: "edit",
    announcements: "edit",
    eod_stock: "edit",
  },
  ops_manager: {
    schedule: "edit",
    shift: "view",
    leave: "edit",
    announcements: "edit",
  },
  hr: {
    schedule: "edit",
    shift: "view",
    leave: "edit",
    announcements: "edit",
    procureiq: "edit",
    hr_dashboard: "view",
  },
};

export const OS_PACK_IDS = new Set(["hq_sales", "os_admin"]);
export const OPS_PACK_IDS = new Set([
  "crew",
  "chef",
  "store_manager",
  "brand_manager",
  "ops_manager",
  "hr",
]);

export function loginKindLabel(kind) {
  return kind === "limited" ? "Ops login" : "HQ login";
}

export function scopeForOpsRole(opsRole) {
  if (opsRole === "employee") return "self";
  if (opsRole === "store_manager") return "location";
  if (opsRole === "brand_manager") return "brand";
  if (opsRole === "ops_manager" || opsRole === "hr") return "all";
  return null;
}

/** @deprecated Prefer scopeForOpsRole — kept for preset pack apply. */
export function scopeForPreset(preset) {
  if (preset === "crew" || preset === "chef") return "self";
  if (preset === "store_manager") return "location";
  if (preset === "brand_manager") return "brand";
  if (
    preset === "hr" ||
    preset === "ops_manager" ||
    preset === "hq_sales" ||
    preset === "os_admin" ||
    preset === "super_admin"
  ) {
    return "all";
  }
  return null;
}

export function opsRoleFromPreset(preset) {
  if (preset === "crew" || preset === "chef") return "employee";
  if (
    preset === "store_manager" ||
    preset === "brand_manager" ||
    preset === "ops_manager" ||
    preset === "hr"
  ) {
    return preset;
  }
  return "";
}

export function brandLabel(brand) {
  const key = String(brand?.key || "").trim().toUpperCase();
  const name = brand?.name || String(brand?.id || "");
  return key ? `${name} (${key})` : name;
}

export function storeLabel(store) {
  const type = String(store?.location_type || "").toUpperCase();
  const name = store?.location_name || "";
  if (type && type !== "STORE") return `${name} · ${type}`;
  return name;
}

function norm(name) {
  return String(name || "").trim().toLowerCase();
}

export function matchStore(stores, locationName) {
  const want = norm(locationName);
  if (!want) return undefined;
  return (stores || []).find((s) => norm(s.location_name) === want);
}

export function storesForBrand(stores, brandId) {
  if (!brandId) return [];
  const id = Number(brandId);
  if (!Number.isFinite(id)) return [];
  return (stores || []).filter((s) => s.brand_id === id);
}

/** Synthetic Brand picker value: co-located PG + DiNAS UNIT sites. */
export const PG_DN_BRAND_VALUE = "pg_dn";

export function isPgDnBrandSelection(brandId) {
  return String(brandId || "").trim() === PG_DN_BRAND_VALUE;
}

function isPhysicalStore(store) {
  return String(store?.location_type || "").toUpperCase() === "STORE";
}

/**
 * UNIT sites that have both a PG and a DiNAS STORE child under the same parent.
 * UI shows unit_name; save expands to child location_name values.
 */
export function unitSitesFromStores(stores) {
  const byParent = new Map();
  (stores || []).forEach((s) => {
    if (!isPhysicalStore(s)) return;
    const parentId = s.parent_location_id;
    if (parentId == null || parentId === "") return;
    const key = String(parentId);
    let group = byParent.get(key);
    if (!group) {
      group = {
        parent_location_id: Number(parentId),
        unit_name: String(s.parent_location_name || "").trim() || null,
        children: [],
      };
      byParent.set(key, group);
    }
    if (!group.unit_name && s.parent_location_name) {
      group.unit_name = String(s.parent_location_name).trim();
    }
    group.children.push(s);
  });

  const out = [];
  byParent.forEach((group) => {
    const brands = new Set(
      group.children.map((c) => Number(c.brand_id)).filter((id) => Number.isFinite(id))
    );
    if (!brands.has(15) || !brands.has(13)) return;
    const childNames = [
      ...new Set(
        group.children
          .map((c) => String(c.location_name || "").trim())
          .filter(Boolean)
      ),
    ].sort();
    if (childNames.length < 2) return;
    const unitName =
      group.unit_name ||
      `UNIT-${String(group.parent_location_id)}`;
    out.push({
      parent_location_id: group.parent_location_id,
      unit_name: unitName,
      child_names: childNames,
    });
  });
  return out.sort((a, b) => a.unit_name.localeCompare(b.unit_name));
}

/** Expand a UNIT selection to the physical STORE location_name grants. */
export function expandUnitToStoreNames(stores, unitName) {
  const want = norm(unitName);
  if (!want) return [];
  const site = unitSitesFromStores(stores).find((u) => norm(u.unit_name) === want);
  return site ? [...site.child_names] : [];
}

/**
 * If location scope values are exactly one UNIT's children, return that unit_name.
 * Otherwise null (single-brand / mismatched grant set).
 */
export function unitNameFromLocationScopes(stores, scopeValues) {
  const selected = [
    ...new Set((scopeValues || []).map((v) => String(v || "").trim()).filter(Boolean)),
  ].sort();
  if (selected.length < 2) return null;
  const selectedKey = selected.join("\0");
  for (const site of unitSitesFromStores(stores)) {
    const childKey = [...site.child_names].sort().join("\0");
    if (childKey === selectedKey) return site.unit_name;
  }
  return null;
}

/** Sibling STORE rows under the same parent (co-located PG + DiNAS sites). */
export function siblingStores(stores, locationName) {
  const hit = matchStore(stores, locationName);
  const parentId = hit?.parent_location_id;
  if (parentId == null) return [];
  const self = norm(hit.location_name);
  return (stores || []).filter(
    (s) =>
      isPhysicalStore(s) &&
      Number(s.parent_location_id) === Number(parentId) &&
      norm(s.location_name) !== self
  );
}

export function locationScopeValuesFromGrants(grants) {
  const out = [];
  const seen = new Set();
  (grants || []).forEach((g) => {
    if (g?.app !== "ops" || g?.scope_type !== "location") return;
    const value = String(g.scope_value || "").trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

const ACTION_RANK = { off: 0, view: 1, edit: 2 };

export function isOpsModuleLocked(opsRole, moduleId) {
  const req = OPS_ROLE_REQUIRED_MODULES[opsRole];
  return Boolean(req && req[moduleId]);
}

export function minLevelForOpsModule(opsRole, moduleId) {
  return OPS_ROLE_REQUIRED_MODULES[opsRole]?.[moduleId] || null;
}

export function applyRequiredOpsLevels(levels, opsRole) {
  const req = OPS_ROLE_REQUIRED_MODULES[opsRole];
  if (!req || !levels?.ops) return levels;
  const nextOps = { ...levels.ops };
  Object.entries(req).forEach(([mod, minLevel]) => {
    const cur = nextOps[mod] || "off";
    if ((ACTION_RANK[cur] || 0) < (ACTION_RANK[minLevel] || 0)) {
      nextOps[mod] = minLevel;
    }
  });
  return { ...levels, ops: nextOps };
}

export function clampOpsLevelChange(opsRole, moduleId, nextLevel) {
  const min = minLevelForOpsModule(opsRole, moduleId);
  if (!min) return nextLevel;
  if ((ACTION_RANK[nextLevel] || 0) < (ACTION_RANK[min] || 0)) return min;
  return nextLevel;
}
